/**
 * Algolia types for storefront-multibrand.
 *
 * Based on breitling-storefront-next types (src/integrations/algolia/types.ts)
 * with simplifications for the bespoke implementation.
 */

// =============================================================================
// Product Types
// =============================================================================

/**
 * Watch product hit from Algolia index.
 */
export interface WatchHit {
  /** SKU - the unique identifier */
  objectID: string;

  // Display
  name: Record<string, string>;
  image_url: string | null;
  thumbnail_url: string | null;

  // Pricing
  price: number;
  currency: string;
  price_range: string;

  // Classification
  product_type: 'Watch';
  collection: string;
  collection_id: string;
  collections: Array<{ name: string; slug: string }>;
  model: {
    name: Record<string, string>;
    slug: string;
  };

  // Attributes (locale-specific - keyed by locale code e.g., "EN_US")
  size: number;
  size_range: string;
  case: Record<string, string>;
  dial_color: Record<string, string>;
  type: Record<string, string[]>;

  // Availability
  ecomm_available: boolean;
  phone_sale_available: boolean;
  call_to_action: string;

  // Product configurator timestamps (for release scheduling)
  plp_release_ts: number | null;
  plp_expire_ts: number | null;
  plp_preview: boolean;
  pdp_release_ts: number | null;
  pdp_expire_ts: number | null;
  pdp_preview: boolean;
  /** Algolia PDP visibility. Absent/`undefined` is treated as enabled. */
  pdp_enabled?: boolean;
  search_release_ts: number | null;
  search_expire_ts: number | null;
  search_preview: boolean;
  components_release_ts: number | null;
  components_expire_ts: number | null;
  components_preview: boolean;

  // Marketing
  limited_edition: boolean;
  marketing_status: string[] | null;
  designer_choice: boolean;
  has_launch_date: boolean;
  launch_date: number;

  // Metadata
  variant_id: string;
  publication_date: number;
  country_code?: string;

  // Highlight results (from search)
  _highlightResult?: Record<string, HighlightResult>;
}

/**
 * Strap product hit from Algolia index.
 */
export interface StrapHit {
  objectID: string;

  // Display
  name: Record<string, string>;
  thumbnail_url: string | null;
  image_url: string | null;

  // Pricing
  price: number;
  currency: string;
  price_range: string;

  // Classification
  product_type: 'Strap';
  material: Record<string, string>;
  material_en: string;
  color: Record<string, string>;
  buckle_type: Record<string, string>;
  strap_length: Record<string, string>;
  strap_length_en: string;

  // Sizing
  distance_between_horns: string;
  lug_size: string;
  other_sizes: string[];
  integrated_buckle: boolean;

  // Availability
  ecomm_available: boolean;
  phone_sale_available: boolean;
  call_to_action: string;

  // Product configurator timestamps
  plp_release_ts: number | null;
  plp_expire_ts: number | null;
  plp_preview: boolean;
  pdp_release_ts: number | null;
  pdp_expire_ts: number | null;
  pdp_preview: boolean;
  /** Algolia PDP visibility. Absent/`undefined` is treated as enabled. */
  pdp_enabled?: boolean;
  search_release_ts: number | null;
  search_expire_ts: number | null;
  search_preview: boolean;
  components_release_ts: number | null;
  components_expire_ts: number | null;
  components_preview: boolean;

  // Metadata
  variant_id: string;
  variant_group: string[];
  publication_date: number;
  country_code?: string;

  _highlightResult?: Record<string, HighlightResult>;
}

/**
 * Highlight result from Algolia search.
 */
export interface HighlightResult {
  matchLevel: 'full' | 'partial' | 'none';
  matchedWords: string[];
  value: string;
}

/**
 * Union type for all product types.
 */
export type ProductHit = WatchHit | StrapHit;

// =============================================================================
// Search Types
// =============================================================================

/**
 * Sort options for product listing.
 */
export type SortOption = 'relevance' | 'price_asc' | 'price_desc' | 'newest';

/**
 * Filter state derived from URL parameters.
 *
 * ## Filter Logic:
 *
 * Currently ALL filters use OR logic (comma separator):
 * - `?collection=Navitimer,Superocean` → Navitimer OR Superocean
 * - `?type=Limited,Gold` → Limited OR Gold
 *
 * ## Future AND Support:
 *
 * Infrastructure for AND logic (plus separator) exists but is unused.
 * To enable AND for a filter:
 * 1. Change parser in useSearchParams.ts from parseAsOrArray to parseAsAndArray
 * 2. Change join in filters.ts from ' OR ' to ' AND '
 *
 * Filter IDs are language-agnostic and persist across locale switches.
 */
export interface FilterState {
  /** Search query text */
  query?: string;

  /** SKU/Reference filter (exact match) */
  sku?: string;

  /**
   * Collection filter - OR logic (comma-separated in URL)
   * Multiple values show products from ANY selected collection
   */
  collection?: string[];

  /**
   * Size filter - OR logic (comma-separated in URL)
   * Multiple values show products matching ANY selected size
   */
  size?: string[];

  /**
   * Case material filter - OR logic (comma-separated in URL)
   * Multiple values show products matching ANY selected material
   */
  case?: string[];

  /**
   * Dial color filter - OR logic (comma-separated in URL)
   * Multiple values show products matching ANY selected color
   */
  dialColor?: string[];

  /**
   * Watch type filter - OR logic (comma-separated in URL)
   * Multiple values show products matching ANY selected type
   */
  type?: string[];

  // ============================================
  // Watch bracelet category filters (Gallet)
  // ============================================

  /**
   * Watch-level bracelet material category filter - OR logic.
   * Uses singular pipe-format attribute bracelet_category_material.{LOCALE}
   * (BEC-24331). URL stores stable IDs after `|`.
   */
  braceletMaterial?: string[];

  /**
   * Watch-level bracelet color category filter - OR logic.
   * Uses singular pipe-format attribute bracelet_category_color.{LOCALE}
   * (BEC-24331). URL stores stable IDs after `|`.
   */
  braceletColor?: string[];

  // ============================================
  // Strap-specific filters
  // ============================================

  /**
   * Strap material filter - OR logic
   * Multiple values show straps matching ANY selected material
   */
  material?: string[];

  /**
   * Strap color filter - OR logic
   * Multiple values show straps matching ANY selected color
   */
  color?: string[];

  /**
   * Lug width filter - OR logic
   * Multiple values show straps matching ANY selected lug width
   */
  lug?: string[];

  /**
   * Lug type filter - OR logic
   * Multiple values show straps matching ANY selected lug type
   */
  lugType?: string[];

  /**
   * Buckle filter - OR logic
   * Multiple values show straps matching ANY selected buckle
   */
  buckle?: string[];

  // ============================================
  // Eyewear-specific filters
  // ============================================

  /**
   * Gender filter - OR logic
   * Multiple values show eyewear matching ANY selected gender
   */
  gender?: string[];

  /**
   * Frame shape filter - OR logic
   * Multiple values show eyewear matching ANY selected frame shape
   */
  frameShape?: string[];

  /**
   * Frame material filter - OR logic
   * Multiple values show eyewear matching ANY selected frame material
   */
  frameMaterial?: string[];

  /**
   * Frame color filter - OR logic
   * Multiple values show eyewear matching ANY selected frame color
   */
  frameColor?: string[];

  /**
   * Lens color filter - OR logic
   * Multiple values show eyewear matching ANY selected lens color
   */
  lensColor?: string[];

  /** Minimum price */
  priceMin?: number;

  /** Maximum price */
  priceMax?: number;

  /** Current page (1-indexed) */
  page?: number;

  /** Sort order */
  sortBy?: SortOption;

  /**
   * Additional custom filter string to combine with standard filters.
   * Used for base filters like strap length or material filters.
   * Example: "(strap_length.EN_US:'Standard')"
   */
  customFilter?: string;

  /**
   * Algolia rule name for editorial pages.
   * Used as the query string to trigger Algolia query rules.
   */
  algoliaRuleName?: string;

  /**
   * Algolia category page ID for editorial pages.
   * Filters products by category_page_id attribute.
   */
  algoliaCategoryId?: string;
}

/**
 * Facet results from Algolia search.
 * Maps facet name to value → count pairs.
 */
export interface FacetResults {
  [facetName: string]: {
    [value: string]: number;
  };
}

/**
 * Complete search result.
 */
export interface SearchResult<T = WatchHit> {
  /** Array of product hits */
  hits: T[];

  /** Total number of matching products */
  totalHits: number;

  /** Total number of pages */
  totalPages: number;

  /** Current page (1-indexed) */
  page: number;

  /** Number of hits per page */
  hitsPerPage: number;

  /** Facet values with counts */
  facets: FacetResults;

  /** Query ID for analytics (click tracking) */
  queryID?: string;

  /** Search processing time in milliseconds */
  processingTimeMs: number;
}

/**
 * Parameters for the search function.
 */
export interface SearchParams {
  /** Locale string (e.g., 'us-en') */
  locale: string;

  /** Filter state */
  filters?: FilterState;

  /** Override default hits per page */
  hitsPerPage?: number;

  /** Specific facets to request (defaults to standard watch facets) */
  facets?: string[];

  /** Enable draft/preview mode */
  draftMode?: boolean;
}

// =============================================================================
// Analytics Types
// =============================================================================

/**
 * Algolia analytics event types.
 */
export enum AlgoliaEventType {
  /** User clicked on a product after search */
  ClickedAfterSearch = 'clickedObjectIDsAfterSearch',

  /** User converted (e.g., viewed PDP) after search */
  ConvertedAfterSearch = 'convertedObjectIDsAfterSearch',

  /** User added to cart after search */
  AddedToCartAfterSearch = 'addedToCartObjectIDsAfterSearch',

  /** User purchased after search */
  PurchasedAfterSearch = 'purchasedObjectIDsAfterSearch',

  /** User clicked on a product (no search context) */
  Clicked = 'clickedObjectIDs',

  /** User converted (no search context) */
  Converted = 'convertedObjectIDs',

  /** User added to cart (no search context) */
  AddedToCart = 'addedToCartObjectIDs',

  /** User purchased (no search context) */
  Purchased = 'purchasedObjectIDs',
}

/**
 * Click event data for analytics.
 */
export interface ClickEventData {
  /** Index name */
  index: string;

  /** Object IDs that were clicked */
  objectIDs: string[];

  /** Query ID from search (for "after search" events) */
  queryID?: string;

  /** Positions in the search results (1-indexed) */
  positions?: number[];
}

/**
 * Conversion event data for analytics.
 */
export interface ConversionEventData {
  /** Index name */
  index: string;

  /** Object IDs that were converted */
  objectIDs: string[];

  /** Query ID from search (for "after search" events) */
  queryID?: string;

  /** Total value of the conversion */
  value?: number;

  /** Currency code */
  currency?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if a product hit is a watch.
 */
export function isWatchHit(hit: ProductHit): hit is WatchHit {
  return hit.product_type === 'Watch';
}

/**
 * Check if a product hit is a strap.
 */
export function isStrapHit(hit: ProductHit): hit is StrapHit {
  return hit.product_type === 'Strap';
}
